import { distinctUntilChanged, filter, map, pairwise, withLatestFrom } from 'rxjs/operators';
import { store, store$ } from '@redux/store';
import { 
  getGeoCoordinates,
  mapStateActions,
  isMapLibraryLoaded,
  isSearchWithinMapEnabled
} from 'features/searchResultsMap';
import { LocationType, searchParametersActions } from 'features/searchResultsFacets';
import { fromEvent } from 'rxjs';

const searchWithinMapButton = document.querySelector('[data-search-with-map-trigger]');

/**
 * When the user clicks the button, the mapStateActions.enableSearchWithinMap() function is called,
 * which updates the state of the map setting the isSearchWithinMap flag in true
 * to enable the search within map functionality.
 */
const enableMapSearch = () => {
  store.dispatch(mapStateActions.enableSearchWithinMap({}));    
  searchWithinMapButton.hidden = true;
};

/**
 * When the user search for new location, the mapStateActions.disableSearchWithinMap() function is called,
 * which updates the state of the map setting the isSearchWithinMap flag in false
 * to disable the search within map functionality.
 */
const disableMapSearch = () => {
  store.dispatch(mapStateActions.disableSearchWithinMap({}));
  searchWithinMapButton.hidden = false;
};

/**
 * It takes the coordinates of the map's current viewport and uses them to update the location parameters
 * from the state which will force an update on the page results.
 */
const fetchSearchWithinMapResults = () => {
  const geoCoordinates = getGeoCoordinates();
  store.dispatch(
    searchParametersActions.fetchLocations({
      locationType: 'rectangularArea',
      minimumLatitude: geoCoordinates.south,
      maximumLatitude: geoCoordinates.north,
      minimumLongitude: geoCoordinates.west,
      maximumLongitude: geoCoordinates.east,
    })
  );
};

/** Unchecks the as needed.
 * Usualy used when the search within map is disabled via other means other that the itself.
 */
const showSearchWithinMapButton = () => {
  searchWithinMapButton.hidden = false;
};

/** Method to be called once search within map is disabled by other means other that the itself.
 * example, once the location is updated via typeahed, then search within map is disabled,
 * so we need to ensure that the is hidden as well
 */
const disableSearchWithinMap = () => {
  disableMapSearch();
  showSearchWithinMapButton();
};

/**
 * hideSearchWithinMapButton: checks the search within map check box.
 */
const hideSearchWithinMapButton = () => {
  searchWithinMapButton.hidden = true;
}

/** Determines the toggle state
 * When the toggle is clicked, it will determine the checked attribute
 * and incoke the eneable or disable map search methods. 
 */
const searchWithinMapToggleAction = (event) => {
  const lement = event.target;
  if (lement.hidden) {
    disableMapSearch();
  } else {
    enableMapSearch();
  }
}

const searchWithinMapButtonClick$ = fromEvent(searchWithinMapButton, 'click');
searchWithinMapButtonClick$.subscribe(searchWithinMapToggleAction);


/** Subscribing to the store.
 * When the location change and it is not RectangularArea, it will call disableMapSearch.
 */
store$
  .pipe(
    map((state) => state.searchParameters.location?.locationType),
    distinctUntilChanged(),
    pairwise(),
    filter(([previousLocationType]) => previousLocationType === LocationType.RectangularArea.toString())
  )
  .subscribe(disableSearchWithinMap);

/**
 * This observable emits a flag indicating when the map library script loaded correctly.
 */
const isMapLibraryLoaded$ = store$.pipe(
  filter(isMapLibraryLoaded),
  filter((state) => state.mapState.isSearchWithinMapReady),
  map(isMapLibraryLoaded),
  distinctUntilChanged());

/**
 * This observable emits new geo coordinates retrieved after the map positioned to a new place.
 */
const onNewCoordinates$ = store$.pipe(
  filter(isMapLibraryLoaded),
  filter((state) => state.mapState.isSearchWithinMapReady),
  map((state) => state.mapState.geoCoordinates),
  distinctUntilChanged());

/** Subscribing to the store.
 *  When the map state geo coordinatnes changes it will call the fetchSearchWithinMapResults function.
 */
store$
  .pipe(
    filter(isMapLibraryLoaded),
    filter((state) => state.mapState.isSearchWithinMapReady),
    map((state) => state.mapState.geoCoordinates),
    /**
     * the explanation behind the pairwise is: we have map coordinates until the time the GM library is loaded and
     * an action stores those geo-coordinates. Before that time, our geo-coordinates are undefined.
     * By using a pairwise operator, we make sure to trigger the search within map functionality only when the
     * user drags the map around from a given location to a new one (so the geo-coordinates in the store changed
     * from valid ones, to a new ones also valid, not undefined).
     * This means: if the geo-coordinates changes from undefined to an actual latitude or longitude value, we are'nt
     * triggering the fetch functionality, because that change means the GM map has loaded and stored the first set
     * of valid geo-coordinates, but not because of an actual drag on the map. The only time that search within map
     * is ON by the time we move from undefined geo-coordinates to valid ones is in initial load for rectangular
     * area searches, and in those scenarios we already have both the map markers and the cards calculated from
     * server side, no need to fetch them again.
     */
    pairwise(),
    filter(([prevCoordinates, currentCoordinates]) => prevCoordinates && currentCoordinates),
    filter(([prevCoordinates, currentCoordinates]) =>
        prevCoordinates.east !== currentCoordinates.east ||
        prevCoordinates.north !== currentCoordinates.north ||
        prevCoordinates.south !== currentCoordinates.south ||
        prevCoordinates.west !== currentCoordinates.west),
    map(([, currentCoordinates]) => currentCoordinates),
    distinctUntilChanged()
  )
  .subscribe(fetchSearchWithinMapResults);

/**
 * Subscribing to the observable for 'Search Within Map' clicks, we fetch results when new clicks are received,
 * provided that signals from the observables for map library loaded and new geo-coordinates have already been emitted.
 */
searchWithinMapButtonClick$.pipe(
  withLatestFrom(isMapLibraryLoaded$, onNewCoordinates$),
  map(([clickEvent]) => ({
    clickEvent
  })),
  filter(({ clickEvent }) => clickEvent.target.hidden)
)
.subscribe(fetchSearchWithinMapResults);

  /** Subscribing to the store.
   *  When search within map is enabled, but the switch on screen for search within map doesn't reflects that,
   *  then we manually check that switch so it is clear for the user that she/he is search within map.
   *  The scenario where this could happen is when an outside module dispatches the enableSearchWithinMap action from
   *  the map slice, for example: the geoProximityHandler.js module when an user selects current location from the
   *  typeahead, this option triggers a search within map search without the need of clicking the search within
   *  map 
   */
  store$
  .pipe(
    filter(() => !searchWithinMapButton.hidden),
    filter(isSearchWithinMapEnabled),
  )
  .subscribe(hideSearchWithinMapButton);